import useVuelidate from '@vuelidate/core';
import { helpers, required, url as urlRule } from '@vuelidate/validators';
import { computed, reactive, watch } from 'vue';

import useSalesforceProxyGate from '@/composables/useSalesforceProxyGate';
import type { DataSourceType } from '@/types/sources';

export type SalesforceProxyFieldType = 'proxyUrl' | 'clientId' | 'clientSecret';

type ConnectionFieldsType = Record<SalesforceProxyFieldType, string>;
type ConnectionPropertiesType = {
  base_url: string;
  client_id: string;
  client_secret: string;
};

const REQUIRED_MESSAGE = 'Proxy URL is required';
const UNPARSEABLE_MESSAGE =
  'Enter a valid URL, for example https://sf-proxy.acme.com';
const SCHEME_MESSAGE = 'The proxy URL must start with https://';
const CREDENTIALS_MESSAGE = 'Remove the username and password from the URL';
const PATH_MESSAGE = 'Enter the host only, without a path';
const QUERY_MESSAGE = 'Enter the host only, without a query string';
const FRAGMENT_MESSAGE = 'Enter the host only, without a fragment';
const PORT_MESSAGE = 'Enter the host only, without a port';
const TRAILING_DOT_MESSAGE = 'Enter the host without a trailing dot';
const IP_LITERAL_MESSAGE = 'Enter a host name, not an IP address';
const HOST_MESSAGE = 'Enter a public host name, for example sf-proxy.acme.com';
const CLIENT_ID_REQUIRED_MESSAGE = 'Client ID is required';
const CLIENT_SECRET_REQUIRED_MESSAGE = 'Client Secret is required';

const FIELD_NAMES: SalesforceProxyFieldType[] = [
  'proxyUrl',
  'clientId',
  'clientSecret',
];
const CLIENT_SECRET_PREFIX = /^client[ _]secret/i;
const CLIENT_ID_PREFIX = /^client[ _]id/i;
const ASCII_PRINTABLE = /^[\x21-\x7e]+$/;
const IP_LITERAL = /^\[|^[\d.]+$/;

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function shapeError(url: URL): string {
  if (url.protocol !== 'https:') return SCHEME_MESSAGE;
  if (url.username || url.password) return CREDENTIALS_MESSAGE;
  if (url.pathname !== '/') return PATH_MESSAGE;
  if (url.search) return QUERY_MESSAGE;
  if (url.hash) return FRAGMENT_MESSAGE;
  if (url.port) return PORT_MESSAGE;
  if (url.hostname.endsWith('.')) return TRAILING_DOT_MESSAGE;
  return '';
}

function hostError(url: URL): string {
  if (IP_LITERAL.test(url.hostname)) return IP_LITERAL_MESSAGE;
  if (urlRule.$validator(url.origin, null, null) !== true) return HOST_MESSAGE;
  return '';
}

function rewriteError(typed: string, url: URL): string {
  return typed.toLowerCase().startsWith(url.origin) ? '' : UNPARSEABLE_MESSAGE;
}

function parseProxyUrl(value: string): { url: URL | null; error: string } {
  const trimmed = value.trim();
  if (!trimmed) return { url: null, error: REQUIRED_MESSAGE };

  const url = ASCII_PRINTABLE.test(trimmed) ? parseUrl(trimmed) : null;
  if (!url) return { url: null, error: UNPARSEABLE_MESSAGE };

  const error = shapeError(url) || rewriteError(trimmed, url) || hostError(url);
  return { url: error ? null : url, error };
}

function connectionProperties(
  values: ConnectionFieldsType,
): ConnectionPropertiesType | null {
  const { url } = parseProxyUrl(values.proxyUrl);
  const clientId = values.clientId.trim();
  const clientSecret = values.clientSecret.trim();
  if (!url || !clientId || !clientSecret) return null;
  return {
    base_url: url.origin,
    client_id: clientId,
    client_secret: clientSecret,
  };
}

function fieldForServerError(message: string): SalesforceProxyFieldType {
  if (CLIENT_SECRET_PREFIX.test(message)) return 'clientSecret';
  if (CLIENT_ID_PREFIX.test(message)) return 'clientId';
  return 'proxyUrl';
}

function emptyFields(): ConnectionFieldsType {
  return { proxyUrl: '', clientId: '', clientSecret: '' };
}

export default function useSalesforceProxyConnection() {
  const { isProxyUrlApplicable } = useSalesforceProxyGate();

  const fields = reactive(emptyFields());
  const serverErrors = reactive(emptyFields());

  const rules = {
    proxyUrl: {
      shape: helpers.withMessage(
        ({ $model }: { $model: string }) => parseProxyUrl($model).error,
        (value: string) => !parseProxyUrl(value).error,
      ),
    },
    clientId: {
      required: helpers.withMessage(CLIENT_ID_REQUIRED_MESSAGE, required),
    },
    clientSecret: {
      required: helpers.withMessage(CLIENT_SECRET_REQUIRED_MESSAGE, required),
    },
  };

  const v$ = useVuelidate(rules, fields, { $lazy: true });

  FIELD_NAMES.forEach((field) => {
    watch(
      () => fields[field],
      () => {
        serverErrors[field] = '';
      },
      { flush: 'sync' },
    );
  });

  function setServerError(message: string) {
    Object.assign(serverErrors, emptyFields());
    if (message) serverErrors[fieldForServerError(message)] = message;
  }

  const isValid = computed(() => Boolean(connectionProperties(fields)));

  function validationError(field: SalesforceProxyFieldType): string {
    if (!v$.value[field].$error) return '';
    return String(v$.value[field].$errors[0]?.$message ?? '');
  }

  function fieldError(field: SalesforceProxyFieldType): string {
    return serverErrors[field] || validationError(field);
  }

  const fieldErrors = computed<ConnectionFieldsType>(() => ({
    proxyUrl: fieldError('proxyUrl'),
    clientId: fieldError('clientId'),
    clientSecret: fieldError('clientSecret'),
  }));

  function touch(field: SalesforceProxyFieldType) {
    v$.value[field].$touch();
  }

  function touchAll() {
    v$.value.$touch();
  }

  function proxyFormPropertiesFor(
    integrationType: DataSourceType | null,
  ): ConnectionPropertiesType | Record<string, never> {
    if (!isProxyUrlApplicable(integrationType)) return {};
    return connectionProperties(fields) ?? {};
  }

  return {
    fields,
    setServerError,
    isValid,
    isProxyUrlApplicable,
    fieldErrors,
    touch,
    touchAll,
    proxyFormPropertiesFor,
  };
}
